import { addDays, isAfter, isBefore } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { formatDateForAPI } from "utils/utils";
import { getTimesheetUnlocksItem } from "components/timesheet/api";
import { timesheetKeys } from "components/timesheet/queries";
import { useFetch } from "hooks/src/useFetch";

export function useLockTimesheet({ date }) {
  // Debug: Check if QueryClient is available

  const { data, isLoading, isError } = useQuery({
    queryKey: timesheetKeys.unlock(date),
    queryFn: () => getTimesheetUnlocksItem({ date }),
    enabled: !!date,
  });

  const today = formatDateForAPI(new Date());
  const sevenDaysAgo = formatDateForAPI(addDays(today, -7));

  // Check if date is older than 7 days OR in the future
  const isOlderThanSevenDays = isBefore(date, sevenDaysAgo);
  const isFutureDate = isAfter(date, today);
  const isDateLocked = isOlderThanSevenDays || isFutureDate;

  // Check if timesheet has been unlocked
  const hasUnlockData = data && data?.length > 0;

  // Determine if user can edit
  const canEdit = !isDateLocked || hasUnlockData;

  // Get lock reason
  let lockReason = "";
  if (isFutureDate) {
    lockReason = "Future dates cannot be edited until they occur.";
  } else if (isOlderThanSevenDays && !hasUnlockData) {
    lockReason = "Entries older than 7 days are locked for data integrity.";
  }

  return {
    isLocked: isDateLocked && !hasUnlockData,
    isLoading,
    isError,
    canEdit,
    lockReason,
    isDateLocked,
    hasUnlockData,
  };
}
